import { Eye, EyeOff, Trash2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { oklchToCss, oklchToHex } from "@/document/model/color";
import type { DropShadowEffect, InnerShadowEffect, LayerBlurEffect, NodeEffect, NodeStyle } from "@/document/model/styles";
import { DraggablePanel } from "./DraggablePanel";
import { effectTypeLabel, effectOptions } from "./effectLabels";
import { Metric } from "./Metric";
import { PanelSelect, PanelSelectItem } from "./PanelSelect";
import { type EffectType, styleWithConvertedEffect, styleWithoutEffect, styleWithUpdatedEffect } from "./paintStyles";

export function EffectEditorPopover({
  style,
  effect,
  suspendDismiss = false,
  onStyleChange,
  onColorClick,
  onClose,
}: {
  style: NodeStyle;
  effect: NodeEffect;
  suspendDismiss?: boolean;
  onStyleChange: (style: NodeStyle) => void;
  onColorClick: () => void;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent): void {
      if (suspendDismiss) {
        return;
      }

      const target = event.target as Node;
      if (panelRef.current?.contains(target)) {
        return;
      }

      onClose();
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [onClose, suspendDismiss]);

  function patch(patchValue: Partial<NodeEffect>): void {
    onStyleChange(styleWithUpdatedEffect(style, effect.id, patchValue));
  }

  return (
    <DraggablePanel
      panelRef={panelRef}
      storageKey="effect-editor"
      title={effectTypeLabel(effect.type)}
      widthClass="w-72"
      onClose={onClose}
    >
      <div className="mb-3 flex items-center gap-2">
        <PanelSelect
          value={effect.type}
          onValueChange={value => onStyleChange(styleWithConvertedEffect(style, effect.id, value as EffectType))}
          triggerClassName="min-w-0 flex-1"
        >
          {effectOptions.map(option => (
            <PanelSelectItem key={option.type} value={option.type}>
              {option.label}
            </PanelSelectItem>
          ))}
        </PanelSelect>
        <button
          type="button"
          aria-label={effect.visible ? "Hide effect" : "Show effect"}
          onClick={() => patch({ visible: !effect.visible } as Partial<NodeEffect>)}
          className="grid size-7 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-100"
        >
          {effect.visible ? <Eye className="size-3.5" aria-hidden="true" /> : <EyeOff className="size-3.5" aria-hidden="true" />}
        </button>
        <button
          type="button"
          aria-label="Remove effect"
          onClick={() => {
            onStyleChange(styleWithoutEffect(style, effect.id));
            onClose();
          }}
          className="grid size-7 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-100"
        >
          <Trash2 className="size-3.5" aria-hidden="true" />
        </button>
      </div>

      {effect.type === "dropShadow" || effect.type === "innerShadow" ? (
        <ShadowControls effect={effect} onPatch={patch} onColorClick={onColorClick} />
      ) : effect.type === "layerBlur" ? (
        <LayerBlurControls effect={effect} onPatch={patch} />
      ) : (
        <BackgroundBlurControls effect={effect} onPatch={patch} />
      )}
    </DraggablePanel>
  );
}

function ShadowControls({
  effect,
  onPatch,
  onColorClick,
}: {
  effect: DropShadowEffect | InnerShadowEffect;
  onPatch: (patch: Partial<NodeEffect>) => void;
  onColorClick: () => void;
}) {
  const colorHex = oklchToHex(effect.color).replace("#", "").toUpperCase();
  const opacity = Math.round(effect.color.alpha * 100);

  return (
    <div className="grid gap-2">
      <div className="grid grid-cols-2 gap-2">
        <Metric label="X" value={effect.offsetX} disabled={false} compact onCommit={value => onPatch({ offsetX: value } as Partial<NodeEffect>)} />
        <Metric label="Y" value={effect.offsetY} disabled={false} compact onCommit={value => onPatch({ offsetY: value } as Partial<NodeEffect>)} />
        <Metric label="Blur" value={effect.blur} disabled={false} compact min={0} onCommit={value => onPatch({ blur: Math.max(0, value) } as Partial<NodeEffect>)} />
        <Metric label="Spread" value={effect.spread} disabled={false} compact onCommit={value => onPatch({ spread: value } as Partial<NodeEffect>)} />
      </div>

      <div className="flex h-7 items-center gap-1.5 rounded bg-white/[0.04] px-1.5">
        <button
          type="button"
          aria-label="Shadow color"
          onClick={onColorClick}
          className="size-5 shrink-0 rounded-sm border border-white/20"
          style={{ background: oklchToCss(effect.color) }}
        />
        <button
          type="button"
          onClick={onColorClick}
          className="min-w-0 flex-1 truncate text-left font-mono text-[11px] text-slate-200"
        >
          {colorHex}
        </button>
        <Metric
          label="%"
          value={opacity}
          disabled={false}
          compact
          min={0}
          max={100}
          onCommit={value => onPatch({ color: { ...effect.color, alpha: Math.max(0, Math.min(1, value / 100)) } } as Partial<NodeEffect>)}
        />
      </div>
    </div>
  );
}

function LayerBlurControls({ effect, onPatch }: { effect: LayerBlurEffect; onPatch: (patch: Partial<NodeEffect>) => void }) {
  return (
    <div className="grid gap-2">
      <div className="grid grid-cols-2 rounded bg-white/[0.04] p-0.5">
        {(["uniform", "progressive"] as const).map(mode => (
          <button
            key={mode}
            type="button"
            onClick={() => onPatch({ mode } as Partial<NodeEffect>)}
            className={`h-7 rounded text-[12px] ${effect.mode === mode ? "bg-white/10 text-slate-100" : "text-slate-500 hover:text-slate-200"}`}
          >
            {mode === "uniform" ? "Uniform" : "Progressive"}
          </button>
        ))}
      </div>
      {effect.mode === "uniform" ? (
        <Metric label="Blur" value={effect.blur} disabled={false} min={0} onCommit={value => onPatch({ blur: Math.max(0, value), end: Math.max(0, value) } as Partial<NodeEffect>)} />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Metric label="Start" value={effect.start} disabled={false} compact min={0} onCommit={value => onPatch({ start: Math.max(0, value) } as Partial<NodeEffect>)} />
          <Metric label="End" value={effect.end} disabled={false} compact min={0} onCommit={value => onPatch({ end: Math.max(0, value), blur: Math.max(0, value) } as Partial<NodeEffect>)} />
        </div>
      )}
    </div>
  );
}

function BackgroundBlurControls({ effect, onPatch }: { effect: Extract<NodeEffect, { type: "backgroundBlur" }>; onPatch: (patch: Partial<NodeEffect>) => void }) {
  return (
    <Metric
      label="Blur"
      value={effect.blur}
      disabled={false}
      min={0}
      onCommit={value => onPatch({ blur: Math.max(0, value) } as Partial<NodeEffect>)}
    />
  );
}
